import React from 'react';

import { Select } from '@aline/common';
import { params } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import { PanelHeader, panelHeaderHeightPx, VariableForm } from 'src/components';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { useGetField } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { getIsSignatureFieldFormField, SignatureFieldForm } from '.';

const useStyles = makeStyles()(theme => ({
  errorContainer: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    padding: theme.spacing(0, 2),
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
  loadingContainer: {
    padding: theme.spacing(0, 2),
  },
  loadingRow: {
    marginBottom: theme.spacing(2),
  },
  panelContainer: {
    backgroundColor: theme.palette.common.white,
    height: `calc(100% - ${panelHeaderHeightPx}px)`,
    overflowY: 'auto',
    width: '100%',
  },
}));

interface InputFieldDetailPanelProps {
  newFieldId: UUID;
}

export function InputFieldDetailPanel({
  newFieldId,
}: InputFieldDetailPanelProps): JSX.Element | null {
  const { classes } = useStyles();
  const titleText = 'Add field';
  const { setValue: setNewFieldId } = useDrawerSearchParam(params.rightPanel.newFieldId);
  const { field, loading: fieldLoading } = useGetField({ variables: { id: newFieldId } });

  function handleCancel(): void {
    setNewFieldId(null);
  }

  if (fieldLoading)
    return (
      <>
        <PanelHeader showBackButton text={titleText} onClickBack={handleCancel} />
        <div
          className={classes.panelContainer}
          data-cy={Select.document.editor.rightDrawer.fieldDetailPanel.container}
        >
          <div className={classes.loadingContainer}>
            {Array.from({ length: 3 }).map((_, index) => {
              return <Skeleton key={index} height={64} />;
            })}
          </div>
        </div>
      </>
    );

  if (!field) return null;

  switch (field.__typename) {
    case 'SignatureField': {
      if (!getIsSignatureFieldFormField(field))
        return (
          <>
            <PanelHeader showBackButton text={titleText} onClickBack={handleCancel} />
            <div
              className={classes.panelContainer}
              data-cy={Select.document.editor.rightDrawer.fieldDetailPanel.container}
            >
              <div className={classes.errorContainer}>
                <Typography align="center" variant="subtitle1">
                  Invalid signature field. Contact support.
                </Typography>
              </div>
            </div>
          </>
        );

      return (
        <>
          <PanelHeader showBackButton text={titleText} onClickBack={handleCancel} />
          <div
            className={classes.panelContainer}
            data-cy={Select.document.editor.rightDrawer.fieldDetailPanel.container}
          >
            <SignatureFieldForm signatureField={field} />
          </div>
        </>
      );
    }
    case 'DocumentVariable': {
      return (
        <>
          <PanelHeader showBackButton text={titleText} onClickBack={handleCancel} />
          <div
            className={classes.panelContainer}
            data-cy={Select.document.editor.rightDrawer.fieldDetailPanel.container}
          >
            <VariableForm key={field.id} variable={field} onDone={handleCancel} />
          </div>
        </>
      );
    }
  }
}
